import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Text } from "../../../../../components/Text";
import { ConversationMessage } from "../../../../../model/integration/dto/meta/MetaConversationMessage";
import { WAMetaMessageDirection } from "../../../../../model/integration/types/whatsapp/WAMetaMessageDirection.enum";
import { Rounded } from "../../../../../theme/Rounded";
import { Tooltip } from "../../../../../utils/local-antd";
import { WAUserAvatar } from "./WAUserAvatar";

const SIZE = "1.75rem";

const Circle = styled.span<{ $tone: "main" | "muted" }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${SIZE};
  height: ${SIZE};
  border-radius: ${Rounded.circle};
  background-color: ${({ $tone, theme }) =>
    $tone === "main" ? theme.main : theme.sub};
`;

type Props = {
  message: ConversationMessage;
  /** What to call the customer on hover */
  customerName?: string;
  /** What to call the AI agent on hover */
  agentName?: string;
};

export const WAMessageAvatar: React.FC<Props> = ({
  message,
  customerName,
  agentName,
}) => {
  const { t } = useTranslation();

  if (message.sentByAI) {
    return (
      <Tooltip
        title={agentName || t("integration.whatsapp.meta.aiAgent.messageBadge")}
      >
        <Circle $tone="main">
          <AtiraIcon icon={faRobot} color="white" size="xs" />
        </Circle>
      </Tooltip>
    );
  }

  if (message.campaignId) {
    return (
      <Tooltip
        title={
          message.campaign?.name
            ? t("integration.whatsapp.meta.campaign.messageFrom", {
                name: message.campaign.name,
              })
            : t("integration.whatsapp.meta.campaign.messageBadge")
        }
      >
        <Circle $tone="main">
          <AtiraIcon icon={faBullhorn} color="white" size="xs" />
        </Circle>
      </Tooltip>
    );
  }

  if (message.direction === WAMetaMessageDirection.INCOMING) {
    return (
      <Tooltip title={customerName}>
        <Circle $tone="muted">
          <AtiraIcon icon={faUser} color="darkerSub" size="xs" />
        </Circle>
      </Tooltip>
    );
  }

  const { name, picture } = message.sentBy || {};
  const initial = name?.trim().charAt(0).toUpperCase();

  return (
    <Tooltip title={name || t("integration.whatsapp.meta.sender.team")}>
      {picture || !initial ? (
        <span>
          <WAUserAvatar user={{ name, picture }} size={SIZE} />
        </span>
      ) : (
        <Circle $tone="main">
          <Text color="white" fontSize="xs" fontWeight="bold">
            {initial}
          </Text>
        </Circle>
      )}
    </Tooltip>
  );
};
